/**
 * `/chat` — the agent. Three resizable panes: conversation list · thread · views rail, laid out
 * with `react-resizable-panels` (draggable dividers, persisted proportions, sensible minimums,
 * and a vertical stack on narrow viewports instead of three unreadable slivers).
 *
 * Persisted state (messages, views) comes from `useConversation`; while a reply streams, an
 * optimistic overlay (the user's turn + the in-progress assistant bubble, plus any `view`/
 * `narration`/`tool_call` events) sits on top of it. On `done` the finished turn is written
 * straight into the query cache and the overlay dropped in the same render (see
 * `withFinishedTurn`), so the thread is never momentarily empty while a refetch is in flight.
 */

import { Wrench } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Group, Panel, Separator, useDefaultLayout, usePanelRef } from "react-resizable-panels";
import { useSearchParams } from "react-router";

import {
  agentKeys,
  AgentStreamError,
  type ConversationDetail,
  type DoneEvent,
  type MessageRecord,
  type NarrationRecord,
  type PinRecord,
  queryClient,
  streamAgentMessage,
  type ToolCallRecord,
  useConversation,
  useConversations,
  useCreateConversation,
  useCreateDashboard,
  useCreatePin,
  useDashboards,
  useDeletePin,
  usePins,
  type ViewRecord,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { useLocalStorage } from "@/lib/use-local-storage";
import { useMediaQuery } from "@/lib/use-media-query";
import { cn } from "@/lib/utils";

import { ConversationList } from "./conversation-list";
import type { ChatMessage, ChatMessagePart } from "./message-bubble";
import { Thread } from "./thread";
import { ViewsRail } from "./views-rail";

const SHOW_TOOL_CALLS_KEY = "chat.showToolCalls";
/** Query param naming the conversation to open — the dashboard links into it per pinned view. */
const CONVERSATION_PARAM = "c";
const LIST_COLLAPSED_PX = 48;
const NARROW_QUERY = "(max-width: 860px)";

/** `ApiError`-shaped errors from the generated client carry `status` — used to tell "the agent
 *  isn't deployed yet" (501/503) apart from any other failure. */
function isBackendUnavailable(error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  return status === 501 || status === 503;
}

function mergeViews(persisted: ViewRecord[], pending: ViewRecord[]): ViewRecord[] {
  const byId = new Map<string, ViewRecord>();
  for (const v of [...persisted, ...pending]) {
    byId.set(v.id, v);
  }
  return [...byId.values()].sort((a, b) => b.created_at.localeCompare(a.created_at));
}

type Marker =
  | { offset: number; kind: "tool_call"; toolCall: ToolCallRecord }
  | { offset: number; kind: "narration"; narration: NarrationRecord };

/** Tool calls that never render as a row, even with the toggle on — `narrate`'s argument is the
 *  exact text already shown as a narration bubble, so showing it again as a tool call would be
 *  pure duplication. */
const HIDDEN_TOOL_NAMES = new Set(["narrate"]);

/** Slices the persisted `content` at each tool call's and narration's `text_offset` and
 *  interleaves the resulting text segments with tool-call and narration rows, so a reloaded
 *  conversation renders identically to the live stream. Offsets are sorted defensively (ties
 *  keep a narration before a tool call sharing its offset — the agent says what it's about to do
 *  before doing it — and otherwise their original — parallel-call — order) and clamped into
 *  `[0, content.length]`; several markers sharing one offset produce no empty text segment
 *  between them. */
function partsFromMessage(message: MessageRecord): ChatMessagePart[] {
  const content = message.content ?? "";
  const markers: Marker[] = [
    ...(message.tool_calls ?? [])
      .filter((toolCall) => !HIDDEN_TOOL_NAMES.has(toolCall.name))
      .map((toolCall) => ({
        offset: toolCall.text_offset ?? 0,
        kind: "tool_call" as const,
        toolCall,
      })),
    ...(message.narrations ?? []).map((narration) => ({
      offset: narration.text_offset ?? 0,
      kind: "narration" as const,
      narration,
    })),
  ];
  markers.sort((a, b) => {
    const byOffset = a.offset - b.offset;
    if (byOffset !== 0) {
      return byOffset;
    }
    if (a.kind === b.kind) {
      return 0;
    }
    return a.kind === "narration" ? -1 : 1;
  });

  const parts: ChatMessagePart[] = [];
  let cursor = 0;
  for (const marker of markers) {
    const offset = Math.min(Math.max(marker.offset, 0), content.length);
    if (offset > cursor) {
      parts.push({ kind: "text", text: content.slice(cursor, offset) });
      cursor = offset;
    }
    parts.push(
      marker.kind === "tool_call"
        ? { kind: "tool_call", toolCall: marker.toolCall }
        : { kind: "narration", narration: marker.narration },
    );
  }
  if (cursor < content.length) {
    parts.push({ kind: "text", text: content.slice(cursor) });
  }
  return parts;
}

/** Appends a text delta to the last part if it's text, otherwise starts a new text part — this
 *  is what lets a tool call landing mid-reply split the surrounding text into two segments. */
function appendTextPart(parts: ChatMessagePart[], text: string): ChatMessagePart[] {
  const last = parts[parts.length - 1];
  if (last?.kind === "text") {
    return [...parts.slice(0, -1), { kind: "text", text: last.text + text }];
  }
  return [...parts, { kind: "text", text }];
}

/** Appends a narration delta to the narration with this id, starting a new part when the id is
 *  unseen — this is what makes a narration type out in place, the way the reply does, instead
 *  of appearing whole once its tool call completes. */
function appendNarrationPart(
  parts: ChatMessagePart[],
  id: string,
  text: string,
): ChatMessagePart[] {
  const index = parts.findIndex((p) => p.kind === "narration" && p.narration.id === id);
  const existing = index < 0 ? null : (parts[index] as { narration: NarrationRecord });
  if (!existing) {
    return [...parts, { kind: "narration", narration: { id, text, text_offset: 0 } }];
  }
  const next = [...parts];
  next[index] = {
    kind: "narration",
    narration: { ...existing.narration, text: existing.narration.text + text },
  };
  return next;
}

/** Swaps a streamed narration for the server's own record (same id) — its text is normalised
 *  and length-capped there, so the record wins over whatever the deltas built up. */
function settleNarrationPart(
  parts: ChatMessagePart[],
  narration: NarrationRecord,
): ChatMessagePart[] {
  const index = parts.findIndex((p) => p.kind === "narration" && p.narration.id === narration.id);
  if (index < 0) {
    return [...parts, { kind: "narration", narration }];
  }
  const next = [...parts];
  next[index] = { kind: "narration", narration };
  return next;
}

/**
 * Folds a finished turn into the cached conversation, so the overlay can be dropped in the SAME
 * render that the persisted version appears in.
 *
 * Without this the page blinked on every reply: `done` cleared the optimistic overlay and left
 * the thread empty until the conversation query's refetch came back, which reads as a reload.
 * The turn is written from the `done` payload instead, and the refetch that follows is only a
 * correctness backstop (it swaps the synthesised user turn below for the persisted row) — by
 * then the same content is already on screen, so it changes nothing visible.
 *
 * The user's own turn isn't in the payload (the server only echoes back what the agent
 * produced), so it's reconstructed from what was sent; its local id is replaced by the refetch.
 */
function withFinishedTurn(
  previous: ConversationDetail | null | undefined,
  done: DoneEvent,
  userMessage: { id: string; content: string },
): ConversationDetail {
  const base: ConversationDetail = previous ?? {
    id: done.conversation.id,
    title: done.conversation.title,
    created_at: done.conversation.created_at,
    updated_at: done.conversation.updated_at,
    messages: [],
    views: [],
  };
  const withTitle: ConversationDetail = {
    ...base,
    title: done.conversation.title,
    updated_at: done.conversation.updated_at,
    views: mergeViews(base.views, done.views),
  };
  // A refetch triggered mid-stream (pinning a view invalidates this query) can land the turn
  // here first — appending it again would show the exchange twice.
  if (base.messages.some((m) => m.id === done.message.id)) {
    return withTitle;
  }
  const last = base.messages[base.messages.length - 1];
  const userTurn: MessageRecord = {
    id: userMessage.id,
    role: "user",
    content: userMessage.content,
    view_ids: [],
    tool_calls: [],
    narrations: [],
    created_at: done.message.created_at,
  };
  const persistedUserTurn = last?.role === "user" && last.content === userMessage.content;
  return {
    ...withTitle,
    messages: persistedUserTurn
      ? [...base.messages, done.message]
      : [...base.messages, userTurn, done.message],
  };
}

export function ChatPage() {
  // `?c=<conversation id>` is how the page is deep-linked into — the dashboard's "from
  // conversation" link is the reason it exists — and it's kept in step with the on-screen
  // conversation so a reload or a back/forward lands on the same one.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlConversationId = searchParams.get(CONVERSATION_PARAM);
  const [selectedId, setSelectedId] = useState<string | null>(urlConversationId);
  const [overlayMessages, setOverlayMessages] = useState<ChatMessage[]>([]);
  const [pendingViews, setPendingViews] = useState<ViewRecord[]>([]);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [showToolCalls, setShowToolCalls] = useLocalStorage(SHOW_TOOL_CALLS_KEY, false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // Mirrors `selectedId`, updated SYNCHRONOUSLY (not via an effect) at every point the on-screen
  // conversation changes — a `streamAgentMessage` callback compares its own conversation id
  // against this ref before touching state, so a reply that outlives a conversation switch (or
  // outlives being superseded by a fresh send) can never paint under the wrong conversation.
  const activeConversationIdRef = useRef<string | null>(urlConversationId);

  const isNarrow = useMediaQuery(NARROW_QUERY);
  const listPanelRef = usePanelRef();
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    // Versioned: the stored proportions win over `defaultSize`, so narrowing the list pane
    // below only reaches anyone who has already dragged a divider if the key changes with it.
    id: "chat-panes-v2",
    storage: window.localStorage,
    panelIds: ["list", "thread", "views"],
  });

  const conversationsQuery = useConversations();
  const conversationQuery = useConversation(selectedId);
  const createConversationMutation = useCreateConversation();
  const dashboardsQuery = useDashboards();
  // Unscoped — every pin across every board, so a view already pinned elsewhere can still be
  // unpinned from here without a second fetch per board.
  const pinsQuery = usePins();
  const createPinMutation = useCreatePin();
  const deletePinMutation = useDeletePin();
  const createDashboardMutation = useCreateDashboard();

  const backendDown = isBackendUnavailable(conversationsQuery.error);

  const persisted = conversationQuery.data;
  const displayMessages = useMemo<ChatMessage[]>(() => {
    const base: ChatMessage[] =
      persisted?.messages.map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        parts: m.role === "assistant" ? partsFromMessage(m) : undefined,
      })) ?? [];
    return [...base, ...overlayMessages];
  }, [persisted, overlayMessages]);

  const displayViews = useMemo(
    () => mergeViews(persisted?.views ?? [], pendingViews),
    [persisted, pendingViews],
  );

  // Maps a view to every pin that holds it (across boards) — the lookup `unpinFromBoard` needs
  // to find the pin id for one board, since the server only takes a pin id, not a (view, board)
  // pair. `view.pinned_dashboard_ids` (not this map) is what actually drives the rail's
  // checkmarks — it's the field the server invalidates on every pin/unpin.
  const pinsByViewId = useMemo(() => {
    const map = new Map<string, PinRecord[]>();
    for (const pin of pinsQuery.data ?? []) {
      const list = map.get(pin.view.id);
      if (list) {
        list.push(pin);
      } else {
        map.set(pin.view.id, [pin]);
      }
    }
    return map;
  }, [pinsQuery.data]);

  function pinToBoard(view: ViewRecord, dashboardId: string) {
    createPinMutation.mutate({
      viewId: view.id,
      dashboardId,
      conversationId: view.conversation_id ?? null,
    });
  }

  function unpinFromBoard(view: ViewRecord, dashboardId: string) {
    const pin = pinsByViewId.get(view.id)?.find((p) => p.dashboard_id === dashboardId);
    if (!pin) {
      logger.warn("[chat] no pin found for view/board", view.id, dashboardId);
      return;
    }
    deletePinMutation.mutate({ pinId: pin.id, conversationId: view.conversation_id ?? null });
  }

  async function createDashboardAndPin(view: ViewRecord) {
    try {
      const created = await createDashboardMutation.mutateAsync({});
      if (created) {
        pinToBoard(view, created.id);
      }
    } catch (err) {
      logger.warn("[chat] failed to create a dashboard to pin into", err);
    }
  }

  function toggleListCollapsed() {
    if (listPanelRef.current?.isCollapsed()) {
      listPanelRef.current.expand();
    } else {
      listPanelRef.current?.collapse();
    }
  }

  /** Writes the on-screen conversation into the URL, replacing rather than pushing — switching
   *  conversations isn't a navigation the back button should have to walk through. */
  function syncConversationParam(id: string | null) {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (id) {
          next.set(CONVERSATION_PARAM, id);
        } else {
          next.delete(CONVERSATION_PARAM);
        }
        return next;
      },
      { replace: true },
    );
  }

  /** Aborts any in-flight reply and moves the "currently on screen" pointer — every stream
   *  callback checks this before touching state, so a superseded reply can never paint here. */
  function switchConversation(id: string | null) {
    abortRef.current?.abort();
    abortRef.current = null;
    activeConversationIdRef.current = id;
    setSelectedId(id);
    setOverlayMessages([]);
    setPendingViews([]);
    setStatusText(null);
    setErrorMessage(null);
    setSending(false);
    syncConversationParam(id);
  }

  // Follows a conversation id that arrived from OUTSIDE this component — a deep link from the
  // dashboard, a pasted URL, a back/forward step. Our own switches update the ref first, so
  // they never round-trip through here.
  useEffect(() => {
    if (urlConversationId !== activeConversationIdRef.current) {
      switchConversation(urlConversationId);
    }
  }, [urlConversationId]);

  function handleNewConversation() {
    switchConversation(null);
  }

  async function handleSend(content: string) {
    setErrorMessage(null);

    let conversationId = selectedId;
    if (!conversationId) {
      try {
        const created = await createConversationMutation.mutateAsync({});
        if (!created) {
          throw new Error("no conversation returned");
        }
        conversationId = created.id;
        activeConversationIdRef.current = conversationId;
        setSelectedId(conversationId);
        syncConversationParam(conversationId);
      } catch (err) {
        logger.error("[chat] failed to create conversation", err);
        setErrorMessage("Couldn't start a new conversation — try again.");
        return;
      }
    } else {
      activeConversationIdRef.current = conversationId;
    }

    // Captured once: every callback below re-checks it against the ref rather than trusting its
    // own closure, so a reply that arrives after the user has switched away is a no-op here.
    const streamConversationId = conversationId;
    const isActive = () => activeConversationIdRef.current === streamConversationId;

    const userMsgId = `local-user-${Date.now()}`;
    const assistantMsgId = `local-assistant-${Date.now()}`;
    setOverlayMessages([
      { id: userMsgId, role: "user", content },
      { id: assistantMsgId, role: "assistant", content: "", parts: [], streaming: true },
    ]);
    setSending(true);

    // Defensive — the composer disables while `sending`, so this shouldn't normally find one,
    // but a stale controller left behind by a race is worse than an extra no-op abort.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let assistantParts: ChatMessagePart[] = [];

    try {
      await streamAgentMessage(
        streamConversationId,
        content,
        {
          onTextDelta: (text) => {
            if (!isActive()) {
              return;
            }
            assistantParts = appendTextPart(assistantParts, text);
            setStatusText(null);
            setOverlayMessages((prev) =>
              prev.map((m) => (m.id === assistantMsgId ? { ...m, parts: assistantParts } : m)),
            );
          },
          onStatus: (text) => {
            if (isActive()) {
              setStatusText(text);
            }
          },
          onView: (view) => {
            if (isActive()) {
              setPendingViews((prev) => [...prev, view]);
            }
          },
          onToolCall: (toolCall) => {
            if (!isActive() || HIDDEN_TOOL_NAMES.has(toolCall.name)) {
              return;
            }
            assistantParts = [...assistantParts, { kind: "tool_call", toolCall }];
            setOverlayMessages((prev) =>
              prev.map((m) => (m.id === assistantMsgId ? { ...m, parts: assistantParts } : m)),
            );
          },
          onNarrationDelta: (id, text) => {
            if (!isActive()) {
              return;
            }
            assistantParts = appendNarrationPart(assistantParts, id, text);
            setStatusText(null);
            setOverlayMessages((prev) =>
              prev.map((m) => (m.id === assistantMsgId ? { ...m, parts: assistantParts } : m)),
            );
          },
          onNarration: (narration) => {
            if (!isActive()) {
              return;
            }
            assistantParts = settleNarrationPart(assistantParts, narration);
            setOverlayMessages((prev) =>
              prev.map((m) => (m.id === assistantMsgId ? { ...m, parts: assistantParts } : m)),
            );
          },
          onDone: (payload) => {
            // Both writes are correct regardless of what's on screen right now.
            queryClient.setQueryData<ConversationDetail | null>(
              agentKeys.conversation(streamConversationId),
              (previous) => withFinishedTurn(previous, payload, { id: userMsgId, content }),
            );
            void queryClient.invalidateQueries({
              queryKey: agentKeys.conversation(streamConversationId),
            });
            void queryClient.invalidateQueries({ queryKey: agentKeys.conversations });
            if (!isActive()) {
              return;
            }
            // Safe to drop now — the line above put the same turn in the cache, so this
            // hands the thread over without ever blanking it.
            setOverlayMessages([]);
            setPendingViews([]);
            setStatusText(null);
          },
          onError: (message) => {
            if (!isActive()) {
              return;
            }
            setErrorMessage(message);
            setOverlayMessages((prev) => prev.filter((m) => m.id !== assistantMsgId));
            setStatusText(null);
          },
        },
        controller.signal,
      );
    } catch (err) {
      logger.warn("[chat] stream failed", err);
      if (isActive()) {
        setErrorMessage(
          err instanceof AgentStreamError && (err.status === 501 || err.status === 503)
            ? "The agent isn't available yet — check back soon."
            : err instanceof Error
              ? err.message
              : "The agent didn't respond. Try again.",
        );
        setOverlayMessages((prev) => prev.filter((m) => m.id !== assistantMsgId));
        setStatusText(null);
      }
    } finally {
      if (isActive()) {
        setSending(false);
      }
    }
  }

  const conversationErrorIsMissing = !!selectedId && conversationQuery.error;
  const separatorClass = cn(
    "bg-border hover:bg-primary/30 shrink-0 transition-colors focus-visible:bg-primary/40 outline-none",
    isNarrow ? "h-1.5 w-full cursor-row-resize" : "w-1.5 cursor-col-resize",
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-border flex shrink-0 items-center justify-end gap-2 border-b px-3 py-2">
        <button
          type="button"
          onClick={() => setShowToolCalls((v) => !v)}
          aria-pressed={showToolCalls}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
            showToolCalls
              ? "border-primary bg-primary/10 text-primary"
              : "border-border text-muted-foreground hover:text-foreground",
          )}
        >
          <Wrench className="h-3 w-3" />
          Tool calls
        </button>
      </div>
      {backendDown ? (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <p className="text-muted-foreground max-w-sm text-center text-sm">
            The agent isn&apos;t available yet — the conversational surface is being wired up on the
            backend. Check back soon.
          </p>
        </div>
      ) : (
        <Group
          orientation={isNarrow ? "vertical" : "horizontal"}
          defaultLayout={defaultLayout}
          onLayoutChanged={onLayoutChanged}
          className="min-h-0 flex-1"
        >
          <Panel
            id="list"
            defaultSize="15"
            minSize="9"
            collapsible
            collapsedSize={LIST_COLLAPSED_PX}
            panelRef={listPanelRef}
            onResize={(size) => setListCollapsed(size.inPixels <= LIST_COLLAPSED_PX + 4)}
            className="min-h-0 min-w-0"
          >
            <ConversationList
              selectedId={selectedId}
              onSelect={(id) => switchConversation(id)}
              onNew={handleNewConversation}
              creating={createConversationMutation.isPending}
              collapsed={listCollapsed}
              onToggleCollapsed={toggleListCollapsed}
              onDeleteActive={() => switchConversation(null)}
            />
          </Panel>
          <Separator className={separatorClass} />
          <Panel id="thread" defaultSize="35" minSize="20" className="min-h-0 min-w-0">
            <Thread
              messages={displayMessages}
              statusText={statusText}
              errorMessage={
                errorMessage ??
                (conversationErrorIsMissing ? "Couldn't load this conversation." : null)
              }
              sending={sending}
              disabled={sending}
              disabledReason={null}
              showToolCalls={showToolCalls}
              onSend={(content) => void handleSend(content)}
            />
          </Panel>
          <Separator className={separatorClass} />
          <Panel id="views" defaultSize="50" minSize="20" className="min-h-0 min-w-0">
            <ViewsRail
              views={displayViews}
              dashboards={dashboardsQuery.data ?? []}
              onPin={pinToBoard}
              onUnpin={unpinFromBoard}
              onCreateDashboardAndPin={(view) => void createDashboardAndPin(view)}
              pinPending={
                createPinMutation.isPending ||
                deletePinMutation.isPending ||
                createDashboardMutation.isPending
              }
            />
          </Panel>
        </Group>
      )}
    </div>
  );
}
